import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { toast } from "sonner";
import {
  ClockCounterClockwise, MagnifyingGlass, User, Truck, ReceiptX,
  UsersThree, FunnelSimple, CaretDown, Sparkle, ArrowClockwise,
  Plus, PencilSimple, Trash, PaperPlaneTilt, CheckCircle, X as XIcon,
  DownloadSimple,
} from "@phosphor-icons/react";

/*
  AuditLogPage — timeline of "who did what" across the account.
  Route: /app/audit

  Everyone in the account can view (transparency helps ops teams).
  Owner + Admin see write actions — filtering by action_prefix + caller_id
  supported. Auto-refresh every 30s.
*/

const ACTION_META = {
  // Loads
  "load.created": { icon: Plus, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "Load created", type: "Load" },
  "load.updated": { icon: PencilSimple, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30", label: "Load updated", type: "Load" },
  "load.deleted": { icon: Trash, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", label: "Load deleted", type: "Load" },
  // Invoices
  "invoice.created": { icon: Plus, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "Invoice created", type: "Invoice" },
  "invoice.updated": { icon: PencilSimple, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30", label: "Invoice updated", type: "Invoice" },
  "invoice.status_changed": { icon: PencilSimple, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", label: "Invoice status changed", type: "Invoice" },
  "invoice.deleted": { icon: Trash, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", label: "Invoice deleted", type: "Invoice" },
  // Drivers
  "driver.created": { icon: Plus, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "Driver added", type: "Driver" },
  "driver.updated": { icon: PencilSimple, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30", label: "Driver updated", type: "Driver" },
  "driver.deleted": { icon: Trash, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", label: "Driver removed", type: "Driver" },
  // Team
  "team.invited": { icon: PaperPlaneTilt, color: "text-[#FF9764]", bg: "bg-[#FF5A1F]/10 border-[#FF5A1F]/30", label: "Team member invited", type: "Team" },
  "team.invite_resent": { icon: PaperPlaneTilt, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", label: "Invite resent", type: "Team" },
  "team.invite_accepted": { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "Invite accepted", type: "Team" },
  "team.invite_canceled": { icon: XIcon, color: "text-neutral-400", bg: "bg-neutral-500/10 border-neutral-500/30", label: "Invite canceled", type: "Team" },
  "team.role_changed": { icon: Sparkle, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30", label: "Role changed", type: "Team" },
  "team.member_removed": { icon: Trash, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", label: "Member removed", type: "Team" },
};

const FILTERS = [
  { key: "all", label: "All activity", prefix: null },
  { key: "load", label: "Loads", prefix: "load." },
  { key: "invoice", label: "Invoices", prefix: "invoice." },
  { key: "driver", label: "Drivers", prefix: "driver." },
  { key: "team", label: "Team", prefix: "team." },
];

const ROLE_BADGE = {
  owner: "bg-[#FF5A1F]/15 text-[#FF9764] border-[#FF5A1F]/40",
  admin: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  dispatcher: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  ops_manager: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  accounting: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  viewer: "bg-neutral-500/15 text-neutral-300 border-neutral-500/40",
};

export default function AuditLogPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    const activeFilter = FILTERS.find((f) => f.key === filter);
    const params = new URLSearchParams();
    if (activeFilter?.prefix) params.set("action_prefix", activeFilter.prefix);
    params.set("limit", "200");
    try {
      const { data } = await client.get(`/account/audit-log?${params.toString()}`);
      setData(data);
    } catch {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const activeFilter = FILTERS.find((f) => f.key === filter);
      const params = new URLSearchParams();
      if (activeFilter?.prefix) params.set("action_prefix", activeFilter.prefix);
      const res = await client.get(
        `/account/audit-log/export?${params.toString()}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `audit-log-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit log downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [filter]);

  if (loading) {
    return <div className="p-6 text-neutral-500 text-sm" data-testid="audit-loading">Loading audit log…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="audit-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <ClockCounterClockwise size={11} /> ACTIVITY LOG
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Team activity</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Every load, invoice, driver, and team change with the who + when.
            {data && (
              <span className="text-neutral-400 ml-1">
                <span className="font-medium text-white">{data.total}</span> total events on record.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={exporting || !data?.events?.length}
            data-testid="audit-export-csv-btn"
            className="text-xs text-neutral-400 hover:text-white transition-colors inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/10 hover:border-white/30 disabled:opacity-50"
            title="Download CSV of the current filter"
          >
            <DownloadSimple size={12} weight="bold" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="audit-refresh-btn"
            className="text-xs text-neutral-400 hover:text-white transition-colors inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/10 hover:border-white/30 disabled:opacity-50"
          >
            <ArrowClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="audit-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`audit-filter-${f.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === f.key
                ? "bg-[#FF5A1F] text-black border-[#FF5A1F] font-semibold"
                : "border-white/10 text-neutral-400 hover:border-white/30 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {data.events.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="card-surface p-2 sm:p-3" data-testid="audit-timeline">
          <div className="divide-y divide-white/5">
            {data.events.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EventRow({ event }) {
  const meta = ACTION_META[event.action] || {
    icon: ClockCounterClockwise, color: "text-neutral-400",
    bg: "bg-neutral-500/10 border-neutral-500/30",
    label: event.action, type: "System",
  };
  const Icon = meta.icon;
  const roleClass = ROLE_BADGE[event.caller_role] || ROLE_BADGE.viewer;

  return (
    <div className="flex items-start gap-3 sm:gap-4 py-3 sm:py-4 px-2 sm:px-3 hover:bg-white/[0.02] transition-colors" data-testid={`audit-event-${event.id}`}>
      <div className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 ${meta.bg}`}>
        <Icon size={14} weight="bold" className={meta.color} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium text-white">{meta.label}</span>
          {event.target_label && (
            <span className="text-sm text-neutral-300 truncate" title={event.target_label}>
              · {event.target_label}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1.5 flex-wrap">
          <User size={11} className="text-neutral-600" />
          <span className="font-medium text-neutral-400">
            {event.caller_name || event.caller_email || "unknown"}
          </span>
          <span
            className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full border ${roleClass}`}
          >
            {event.caller_role}
          </span>
          <span className="text-neutral-600">·</span>
          <span title={new Date(event.created_at).toLocaleString()}>{_relTime(event.created_at)}</span>
          {event.details && Object.keys(event.details).length > 0 && (
            <>
              <span className="text-neutral-600">·</span>
              <span className="text-neutral-500 truncate max-w-xs" title={JSON.stringify(event.details)}>
                {_formatDetails(event.details)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ filter }) {
  const activeFilter = FILTERS.find((f) => f.key === filter);
  return (
    <div className="card-surface p-10 text-center" data-testid="audit-empty">
      <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
        <ClockCounterClockwise size={20} className="text-neutral-500" />
      </div>
      <div className="text-sm text-neutral-300 font-medium mb-1">
        No {activeFilter?.label.toLowerCase() || "activity"} yet
      </div>
      <div className="text-xs text-neutral-500 max-w-xs mx-auto leading-relaxed">
        Every load, invoice, driver, and team change your team makes will show up here.
      </div>
    </div>
  );
}

function _relTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.floor((now - then) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return "recently"; }
}

function _formatDetails(details) {
  if (!details || typeof details !== "object") return "";
  // Compact, readable format — no braces, first key/val pair
  const entries = Object.entries(details).slice(0, 2);
  return entries.map(([k, v]) => {
    if (Array.isArray(v)) return `${k}: ${v.join(", ")}`;
    if (v === null || v === undefined) return "";
    return `${k}: ${v}`;
  }).filter(Boolean).join(" · ");
}
